import { useEffect, useRef, useState } from 'react';
import { customFetcher } from './useFetch';
import {
  needsLookup,
  pickFromPool,
} from '../services/recordResolution.service';

/**
 * Löst einen per ID zugeordneten Datensatz für die Anzeige auf und lädt ihn
 * nach, wenn die vorhandene Liste ihn nicht kennt (QS #7).
 *
 * Auswahlfelder leiten ihren Wert aus einer geladenen Liste ab. Kommt diese
 * Liste aus einer Suche, ist sie beim Öffnen der Maske leer — ein zugeordneter
 * Vorgang oder Mandant war dann unauffindbar, und das Feld sah leer aus, obwohl
 * die Zuordnung besteht. Die gespeicherte ID bleibt davon unberührt; sichtbar
 * war sie nur nicht.
 *
 * Jede ID wird höchstens einmal angefragt, auch wenn der Abruf scheitert —
 * sonst dreht ein gelöschter Datensatz eine Endlosschleife.
 */
export const useResolvedRecord = <T, K extends keyof T & string>(
  endpointPrefix: string,
  idField: K,
  id: number | null | undefined,
  pool: T[] | undefined
): T | undefined => {
  const [loaded, setLoaded] = useState<T | undefined>(undefined);
  const requestedIds = useRef<Set<number>>(new Set());

  const fromPool = pickFromPool(pool, idField, id);
  const fromLoaded =
    loaded !== undefined &&
    loaded !== null &&
    (loaded[idField] as unknown) === id
      ? loaded
      : undefined;
  const resolved = fromPool ?? fromLoaded;

  const shouldLoad =
    needsLookup(id, resolved) && !requestedIds.current.has(id as number);

  useEffect(() => {
    if (!shouldLoad || typeof id !== 'number') {
      return;
    }
    requestedIds.current.add(id);

    let cancelled = false;
    customFetcher({ endpoint: `${endpointPrefix}/${id}` })
      .then(record => {
        if (!cancelled && record) setLoaded(record as T);
      })
      // Ein Datensatz, der nicht mehr existiert, darf die Maske nicht stoeren —
      // dann bleibt das Feld eben leer.
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [shouldLoad, id, endpointPrefix]);

  return resolved;
};
